import React, { useState, useCallback, useEffect } from 'react';

/**
 * Media Gallery carousel + lightbox for raffle detail pages.
 * Shows one item at a time with navigation arrows and dot indicators.
 * Clicking opens fullscreen lightbox with keyboard nav.
 */
export default function MediaGallery({ media = [], imageUrl = '' }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  // Build gallery items: use mediaGallery if available, fallback to single imageUrl
  const items = media.length > 0
    ? media
    : imageUrl
      ? [{ url: imageUrl, type: 'image' }]
      : [];

  const openLightbox = (index) => {
    setActiveIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = '';
  };

  const goNext = useCallback(() => {
    setActiveIndex(prev => (prev + 1) % items.length);
  }, [items.length]);

  const goPrev = useCallback(() => {
    setActiveIndex(prev => (prev - 1 + items.length) % items.length);
  }, [items.length]);

  // Keyboard navigation (lightbox only)
  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxOpen, goNext, goPrev]);

  if (items.length === 0) return null;

  const currentItem = items[activeIndex];
  const hasMultiple = items.length > 1;

  return (
    <>
      {/* ─── Main Carousel ─── */}
      <div style={{
        position: 'relative', width: '100%', height: '340px',
        borderRadius: '16px', overflow: 'hidden', marginBottom: '2rem',
        boxShadow: '0 8px 30px rgba(0,0,0,0.12)', background: '#1a1a2e',
      }}>
        {/* Current slide */}
        <div
          onClick={() => openLightbox(activeIndex)}
          style={{ width: '100%', height: '100%', cursor: 'pointer', position: 'relative' }}
        >
          {currentItem.type === 'video' ? (
            <>
              <video src={currentItem.url} style={coverStyle} muted playsInline preload="metadata" />
              <div style={playOverlayStyle}>▶</div>
            </>
          ) : (
            <img src={currentItem.url} alt="Campanha" style={coverStyle} />
          )}
        </div>

        {/* Gradient overlays for arrows */}
        {hasMultiple && (
          <>
            <div style={{
              position: 'absolute', top: 0, left: 0, bottom: 0, width: '80px',
              background: 'linear-gradient(90deg, rgba(0,0,0,0.35), transparent)',
              pointerEvents: 'none',
            }} />
            <div style={{
              position: 'absolute', top: 0, right: 0, bottom: 0, width: '80px',
              background: 'linear-gradient(-90deg, rgba(0,0,0,0.35), transparent)',
              pointerEvents: 'none',
            }} />
          </>
        )}

        {/* Prev arrow */}
        {hasMultiple && (
          <button
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            style={carouselArrowStyle('left')}
            aria-label="Anterior"
          >
            ‹
          </button>
        )}

        {/* Next arrow */}
        {hasMultiple && (
          <button
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            style={carouselArrowStyle('right')}
            aria-label="Próximo"
          >
            ›
          </button>
        )}

        {/* Bottom bar: dots + hint */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          background: 'linear-gradient(transparent, rgba(0,0,0,0.6))',
          padding: '1.5rem 1rem 0.8rem', display: 'flex',
          justifyContent: 'center', alignItems: 'center', gap: '0.6rem',
        }}>
          {/* Dot indicators */}
          {hasMultiple && items.map((_, i) => (
            <button
              key={i}
              onClick={(e) => { e.stopPropagation(); setActiveIndex(i); }}
              style={{
                width: activeIndex === i ? '24px' : '8px',
                height: '8px',
                borderRadius: '4px',
                background: activeIndex === i ? '#fff' : 'rgba(255,255,255,0.5)',
                border: 'none', cursor: 'pointer', padding: 0,
                transition: 'all 0.3s ease',
              }}
              aria-label={`Mídia ${i + 1}`}
            />
          ))}

          {/* Media type badge */}
          <span style={{
            position: 'absolute', right: '1rem', bottom: '0.8rem',
            background: 'rgba(0,0,0,0.5)', color: '#fff', fontSize: '0.75rem',
            padding: '4px 10px', borderRadius: '16px', fontWeight: 600,
          }}>
            {currentItem.type === 'video' ? '▶ Clique para assistir' : '🔍 Clique para ampliar'}
          </span>

          {/* Counter badge */}
          {hasMultiple && (
            <span style={{
              position: 'absolute', left: '1rem', bottom: '0.8rem',
              background: 'rgba(0,0,0,0.5)', color: '#fff', fontSize: '0.75rem',
              padding: '4px 10px', borderRadius: '16px', fontWeight: 600,
            }}>
              {activeIndex + 1} / {items.length}
            </span>
          )}
        </div>
      </div>

      {/* ─── Thumbnail strip (for 2+ items) ─── */}
      {hasMultiple && (
        <div style={{
          display: 'flex', gap: '8px', marginBottom: '1.5rem', marginTop: '-1rem',
          justifyContent: 'center', flexWrap: 'wrap',
        }}>
          {items.map((item, i) => (
            <div
              key={i}
              onClick={() => setActiveIndex(i)}
              style={{
                width: '64px', height: '64px', borderRadius: '10px',
                overflow: 'hidden', cursor: 'pointer', position: 'relative',
                border: activeIndex === i ? '3px solid var(--primary)' : '3px solid transparent',
                opacity: activeIndex === i ? 1 : 0.6,
                transition: 'all 0.2s ease',
                boxShadow: activeIndex === i ? '0 2px 10px rgba(108,92,231,0.4)' : 'none',
              }}
            >
              {item.type === 'video' ? (
                <>
                  <video src={item.url} style={coverStyle} muted playsInline preload="metadata" />
                  <div style={{
                    position: 'absolute', inset: 0, display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(0,0,0,0.3)', color: '#fff', fontSize: '1rem',
                  }}>▶</div>
                </>
              ) : (
                <img src={item.url} alt="" style={coverStyle} />
              )}
            </div>
          ))}
        </div>
      )}

      {/* ─── Lightbox ─── */}
      {lightboxOpen && (
        <Lightbox
          items={items}
          activeIndex={activeIndex}
          onClose={closeLightbox}
          onNext={goNext}
          onPrev={goPrev}
        />
      )}
    </>
  );
}

function Lightbox({ items, activeIndex, onClose, onNext, onPrev }) {
  const item = items[activeIndex];

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.92)', zIndex: 10001,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'mgFadeIn 0.2s ease',
      }}
    >
      {/* Close button */}
      <button onClick={onClose} style={{
        position: 'absolute', top: '1rem', right: '1rem', zIndex: 10,
        background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
        borderRadius: '50%', width: '44px', height: '44px', fontSize: '1.5rem',
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        ✕
      </button>

      {/* Counter */}
      {items.length > 1 && (
        <div style={{
          position: 'absolute', top: '1rem', left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(255,255,255,0.15)', color: '#fff', padding: '0.3rem 1rem',
          borderRadius: '20px', fontSize: '0.85rem', fontWeight: 600,
        }}>
          {activeIndex + 1} / {items.length}
        </div>
      )}

      {/* Prev button */}
      {items.length > 1 && (
        <button onClick={(e) => { e.stopPropagation(); onPrev(); }} style={lightboxArrowStyle('left')}>
          ‹
        </button>
      )}

      {/* Content */}
      <div onClick={(e) => e.stopPropagation()} style={{
        maxWidth: '90vw', maxHeight: '85vh', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
      }}>
        {item.type === 'video' ? (
          <video
            src={item.url}
            controls
            autoPlay
            style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: '8px' }}
          />
        ) : (
          <img
            src={item.url}
            alt=""
            style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: '8px', objectFit: 'contain' }}
          />
        )}
      </div>

      {/* Next button */}
      {items.length > 1 && (
        <button onClick={(e) => { e.stopPropagation(); onNext(); }} style={lightboxArrowStyle('right')}>
          ›
        </button>
      )}

      <style>{`
        @keyframes mgFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
}

// ─── Styles ───

const coverStyle = {
  width: '100%', height: '100%', objectFit: 'cover', display: 'block',
};

const playOverlayStyle = {
  position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
  background: 'rgba(0,0,0,0.5)', borderRadius: '50%', width: '64px', height: '64px',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontSize: '1.8rem', color: '#fff', pointerEvents: 'none',
};

const carouselArrowStyle = (side) => ({
  position: 'absolute',
  [side]: '0.6rem',
  top: '50%',
  transform: 'translateY(-50%)',
  background: 'rgba(255,255,255,0.9)',
  border: 'none',
  color: '#2d3436',
  borderRadius: '50%',
  width: '40px',
  height: '40px',
  fontSize: '1.6rem',
  fontWeight: 700,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.2s',
  zIndex: 5,
  boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
  lineHeight: 1,
});

const lightboxArrowStyle = (side) => ({
  position: 'absolute',
  [side]: '1rem',
  top: '50%',
  transform: 'translateY(-50%)',
  background: 'rgba(255,255,255,0.15)',
  border: 'none',
  color: '#fff',
  borderRadius: '50%',
  width: '48px',
  height: '48px',
  fontSize: '2rem',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'background 0.2s',
  zIndex: 10,
});
